import type * as React from "react";
import { cn } from "../../../lib/cn";
import { CitationHighlighter } from "../CitationHighlighter";
import type { CitationAnchor } from "../types";

export interface DBRowPayload {
  /** SQL that produced the result set. */
  sql: string;
  /** Column order to render. */
  columns: string[];
  /** Each row indexed by column name. */
  rows: Array<Record<string, string | number | boolean | null>>;
  /** Optional human label for the source ("hr.contracts", "Salesforce Lead"…). */
  tableLabel?: string;
}

export interface DBRowViewProps {
  payload: DBRowPayload;
  anchor?: CitationAnchor;
}

/**
 * DBRowView — SQL code block above + result set table below. The cited
 * row is highlighted via `<CitationHighlighter as="tr">`.
 *
 * Scaffolding: relies on rehype-highlight CSS classes already loaded by
 * `MessageBody`. Production swap: shiki tokenisation per cell for richer
 * SQL highlighting — same API.
 */
export const DBRowView: React.FC<DBRowViewProps> = ({ payload, anchor }) => {
  const citedRow = anchor?.rowId;

  return (
    <div className="flex flex-col gap-3">
      <pre
        className={cn(
          "overflow-x-auto rounded-md border border-hairline",
          "bg-ink-1 px-3 py-2 font-mono text-[12px] text-cream leading-relaxed",
        )}
      >
        <code className="language-sql">{payload.sql}</code>
      </pre>

      <div className="overflow-x-auto rounded-md border border-hairline">
        <table className="w-full border-collapse text-xs">
          <thead className="bg-surface-1/80 text-ink-3">
            <tr>
              {payload.columns.map((c) => (
                <th
                  key={c}
                  className={cn(
                    "border-hairline border-b px-2 py-1 text-left",
                    "font-mono text-[10px] uppercase tracking-wide",
                  )}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payload.rows.map((row, idx) => {
              const rowId = String(row.id ?? row.pk ?? idx);
              const isCited = citedRow === rowId;
              return (
                <tr
                  key={rowId}
                  data-row-id={rowId}
                  className={cn(
                    "border-hairline/60 border-b last:border-b-0",
                    isCited && "bg-ando-orange-soft/40",
                  )}
                >
                  {payload.columns.map((c) => {
                    const cell = row[c];
                    const cellText =
                      cell === null || cell === undefined ? "—" : String(cell);
                    return (
                      <td
                        key={c}
                        className={cn(
                          "px-2 py-1.5 align-top font-mono text-[11px] text-ink-1",
                          isCited && "border-ando-orange border-l",
                        )}
                      >
                        {isCited && c === payload.columns[0] ? (
                          <CitationHighlighter className="-mx-1 inline-block px-1">
                            {cellText}
                          </CitationHighlighter>
                        ) : (
                          cellText
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {payload.tableLabel ? (
        <div className="text-[10px] text-ink-3">
          Tabella · <span className="font-mono">{payload.tableLabel}</span>
        </div>
      ) : null}
    </div>
  );
};
DBRowView.displayName = "DBRowView";
